import {
  ChangeEvent,
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  ActiveDot,
  Box,
  Checkbox,
  CheckboxProps,
  FormControl,
  FormControlLabel as BaseFormControlLabel,
  FormHelperText,
  styled,
} from '@sbv-clients/ui-shared';

import { useFormFieldError } from '../../hooks/use-field-error';

type FormCheckboxFieldProps = CheckboxProps & {
  name: string;
  fullWidth?: boolean;
  label: string;
  defaultChecked?: boolean;
  withForm?: boolean;
  isChecked?: boolean;
  checked?: boolean;
  updatedStyling?: boolean;
  active?: boolean;
};

type RenderType = ComponentProps<typeof Controller>['render'];

const FormControlLabel = styled(BaseFormControlLabel)`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.28px;
  margin: 0;
`;

export const FormCheckboxField = ({
  name,
  disabled,
  required,
  fullWidth,
  label,
  size,
  onChange,
  defaultChecked = false,
  withForm,
  checked: checkedProp,
  updatedStyling,
  active,
}: FormCheckboxFieldProps) => {
  const { error, helperText } = useFormFieldError(name);
  const form = useFormContext();
  const [checked, setChecked] = useState(defaultChecked);

  const resolvedLabel = useMemo(
    () =>
      active ? (
        <Box display="flex" alignItems="center" gap={1}>
          {label}
          <ActiveDot />
        </Box>
      ) : (
        label
      ),
    [active, label]
  );

  useEffect(() => {
    setChecked(defaultChecked);
  }, [defaultChecked]);

  const renderWithForm: RenderType = useCallback(
    ({ field }) => (
      <FormControl
        error={error}
        required={required}
        fullWidth={fullWidth}
        disabled={disabled}
        size={size as any}
      >
        <FormControlLabel
          label={resolvedLabel}
          className={size}
          control={
            <Checkbox
              updatedStyling={updatedStyling}
              inputRef={field.ref}
              checked={field.value ?? defaultChecked}
              onBlur={field.onBlur}
              onChange={(event: ChangeEvent<HTMLInputElement>, checked) => {
                field.onChange(checked);
                onChange?.(event, checked);
              }}
              size={size}
            />
          }
        />
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    ),
    [
      error,
      required,
      fullWidth,
      disabled,
      size,
      updatedStyling,
      resolvedLabel,
      defaultChecked,
      helperText,
      onChange,
    ]
  );

  const renderWithoutForm = () => (
    <FormControl
      error={error}
      required={required}
      fullWidth={fullWidth}
      disabled={disabled}
      size={size as any}
    >
      <FormControlLabel
        label={label}
        className={size}
        control={
          <Checkbox
            updatedStyling={updatedStyling}
            checked={typeof checkedProp !== 'undefined' ? checkedProp : checked}
            onChange={(event: ChangeEvent<HTMLInputElement>, checkedValue) => {
              if (typeof checkedProp === 'undefined') {
                setChecked(checkedValue);
              }
              onChange?.(event, checkedValue);
            }}
            size={size}
          />
        }
      />
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );

  if (withForm && form?.control) {
    return (
      <Controller
        name={name}
        control={form.control}
        render={renderWithForm}
        defaultValue={defaultChecked}
      />
    );
  }

  return renderWithoutForm();
};
